import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { publicPath } from '../../utils/canonicalUrl';
import { formatPrice } from '../../utils/format';
import { getMedicoSlug, formatSlotTime } from '../../utils/slug';
import { saveMedicoBookingIntent } from '../../utils/bookingIntent';
import './WidgetPrenotaPage.css';

/**
 * Pagina embed widget struttura (canale = WIDGET).
 * Percorso pubblico: /widget/:slug/
 */
export default function WidgetPrenotaPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isPaziente } = useAuth();

  const [struttura, setStruttura] = useState(null);
  const [results, setResults] = useState([]);
  const [prestazione, setPrestazione] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const cfg = await api.get(`/public/strutture/by-slug/${encodeURIComponent(slug)}`);
        if (cancelled) return;
        setStruttura(cfg.data);
        const search = await api.get('/public/search', {
          params: {
            struttura: slug,
            sort: 'disponibilita',
            ...(prestazione.trim() ? { prestazione: prestazione.trim() } : {}),
          },
        });
        if (cancelled) return;
        setResults(Array.isArray(search.data?.results) ? search.data.results : []);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.status === 404
            ? 'Struttura non trovata.'
            : 'Impossibile caricare le disponibilità.');
          setStruttura(null);
          setResults([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, prestazione]);

  const bookSlot = (hit, slot) => {
    const medico = { id: hit.idMedico, nome: hit.medicoNome, cognome: hit.medicoCognome };
    const medicoSlug = hit.slug || getMedicoSlug(medico);
    const intent = saveMedicoBookingIntent(
      medico,
      medicoSlug,
      slot.data,
      { oraInizio: slot.oraInizio, oraFine: slot.oraFine, disponibile: true },
      {
        idTariffa: hit.idTariffa,
        durataMin: hit.durataMinuti,
        isVideo: hit.isVideo,
        nomePrestazione: hit.nomePrestazione,
        importo: hit.prezzo,
        canale: 'WIDGET',
        idStruttura: hit.idStruttura || struttura?.id,
        idSede: hit.idSede,
        modalita: hit.isVideo ? 'VIDEO' : 'STUDIO',
      }
    );
    if (isAuthenticated() && isPaziente()) {
      navigate(intent.path, { state: intent.state });
    } else {
      navigate('/login', { state: { afterLogin: intent } });
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    setPrestazione(String(fd.get('prestazione') || '').trim());
  };

  return (
    <div className="widget-prenota">
      <header className="widget-prenota__header">
        <div>
          <p className="widget-prenota__kicker">Prenota online</p>
          <h1>{struttura?.nomePubblico || 'Virtual Clinic'}</h1>
        </div>
        <a className="widget-prenota__brand" href={publicPath('/')} target="_blank" rel="noreferrer">
          Powered by Virtual Clinic
        </a>
      </header>

      <form className="widget-prenota__form" onSubmit={onSearch}>
        <label>
          Di cosa hai bisogno
          <input name="prestazione" type="search" defaultValue={prestazione} placeholder="Es. visita cardiologica" />
        </label>
        <button type="submit">Cerca</button>
      </form>

      {loading && <p className="widget-prenota__muted">Caricamento disponibilità…</p>}
      {error && <p className="widget-prenota__error" role="alert">{error}</p>}

      {!loading && !error && results.length === 0 && (
        <p className="widget-prenota__muted">Nessuna disponibilità al momento. Riprova più tardi.</p>
      )}

      <ul className="widget-prenota__list">
        {results.map((hit) => {
          const slugMedico = hit.slug || getMedicoSlug({ id: hit.idMedico, nome: hit.medicoNome, cognome: hit.medicoCognome });
          const name = `${hit.medicoTitolo || 'Dott.'} ${hit.medicoNome || ''} ${hit.medicoCognome || ''}`.trim();
          return (
            <li key={`${hit.idTariffa}-${hit.idMedico}`} className="widget-prenota__card">
              <div className="widget-prenota__card-top">
                <div>
                  <h2>
                    <Link to={publicPath(`/medici/${slugMedico}`)} target="_blank" rel="noreferrer">{name}</Link>
                  </h2>
                  <p>{hit.nomePrestazione || hit.specializzazione}</p>
                </div>
                <div className="widget-prenota__price">{formatPrice(hit.prezzo)}</div>
              </div>
              <div className="widget-prenota__slots">
                {(hit.prossimiSlot || []).map((slot) => (
                  <button
                    key={`${slot.data}-${slot.oraInizio}`}
                    type="button"
                    onClick={() => bookSlot(hit, slot)}
                  >
                    {slot.label || formatSlotTime(slot.oraInizio)}
                  </button>
                ))}
                {(!hit.prossimiSlot || hit.prossimiSlot.length === 0) && (
                  <span className="widget-prenota__muted">Nessuno slot libero</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
